// Auto-Import (Campaign → Projects) Actions
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helpers';
import { dedupedGet } from '../../utils/api/dedupedFetch';

// Action types
export const GET_LINKS_BY_CAMPAIGN = 'GET_LINKS_BY_CAMPAIGN';
export const GET_LINKS_BY_CAMPAIGN_SUCCESS = 'GET_LINKS_BY_CAMPAIGN_SUCCESS';
export const GET_LINKS_BY_CAMPAIGN_FAIL = 'GET_LINKS_BY_CAMPAIGN_FAIL';
export const GET_LINKS_BY_CAMPAIGN_RESET = 'GET_LINKS_BY_CAMPAIGN_RESET';

export const GET_LINKS_BY_PROJECT = 'GET_LINKS_BY_PROJECT';
export const GET_LINKS_BY_PROJECT_SUCCESS = 'GET_LINKS_BY_PROJECT_SUCCESS';
export const GET_LINKS_BY_PROJECT_FAIL = 'GET_LINKS_BY_PROJECT_FAIL';
export const GET_LINKS_BY_PROJECT_RESET = 'GET_LINKS_BY_PROJECT_RESET';

export const GET_LINKABLE_PROJECTS = 'GET_LINKABLE_PROJECTS';
export const GET_LINKABLE_PROJECTS_SUCCESS = 'GET_LINKABLE_PROJECTS_SUCCESS';
export const GET_LINKABLE_PROJECTS_FAIL = 'GET_LINKABLE_PROJECTS_FAIL';
export const GET_LINKABLE_PROJECTS_RESET = 'GET_LINKABLE_PROJECTS_RESET';

export const SAVE_AUTO_IMPORT_LINKS = 'SAVE_AUTO_IMPORT_LINKS';
export const SAVE_AUTO_IMPORT_LINKS_SUCCESS = 'SAVE_AUTO_IMPORT_LINKS_SUCCESS';
export const SAVE_AUTO_IMPORT_LINKS_FAIL = 'SAVE_AUTO_IMPORT_LINKS_FAIL';
export const SAVE_AUTO_IMPORT_LINKS_RESET = 'SAVE_AUTO_IMPORT_LINKS_RESET';

export const FORCE_SYNC_FROM_CAMPAIGN = 'FORCE_SYNC_FROM_CAMPAIGN';
export const FORCE_SYNC_FROM_CAMPAIGN_SUCCESS = 'FORCE_SYNC_FROM_CAMPAIGN_SUCCESS';
export const FORCE_SYNC_FROM_CAMPAIGN_FAIL = 'FORCE_SYNC_FROM_CAMPAIGN_FAIL';
export const FORCE_SYNC_FROM_CAMPAIGN_RESET = 'FORCE_SYNC_FROM_CAMPAIGN_RESET';

export const FORCE_SYNC_FROM_PROJECT = 'FORCE_SYNC_FROM_PROJECT';
export const FORCE_SYNC_FROM_PROJECT_SUCCESS = 'FORCE_SYNC_FROM_PROJECT_SUCCESS';
export const FORCE_SYNC_FROM_PROJECT_FAIL = 'FORCE_SYNC_FROM_PROJECT_FAIL';
export const FORCE_SYNC_FROM_PROJECT_RESET = 'FORCE_SYNC_FROM_PROJECT_RESET';

export const GET_AUTO_SYNC_STATUS = 'GET_AUTO_SYNC_STATUS';
export const GET_AUTO_SYNC_STATUS_SUCCESS = 'GET_AUTO_SYNC_STATUS_SUCCESS';
export const GET_AUTO_SYNC_STATUS_FAIL = 'GET_AUTO_SYNC_STATUS_FAIL';
export const GET_AUTO_SYNC_STATUS_RESET = 'GET_AUTO_SYNC_STATUS_RESET';

// Auth header helper
const authHeader = () => ({ Authorization: 'Bearer ' + getUserToken() });

// Plain request helpers (no Redux). Used by the create-study forms' useAutoImportProjects hook,
// which needs the promise (to navigate after the save) rather than a dispatched state.
// saveAutoImportLinksApi below posts the same { links: [{ project_id, is_active }] } contract.
// A timeout so a stalled request can never leave the form spinning forever.
const HOOK_REQUEST_TIMEOUT_MS = 30000;
export const fetchLinkableProjectsForNewCampaign = () =>
  axios.get(`${config.api.base_url}/auto-import/linkable-projects`, { headers: authHeader(), timeout: HOOK_REQUEST_TIMEOUT_MS });
export const fetchAutoImportLinks = (campaignId) =>
  axios.get(`${config.api.base_url}/campaigns/${campaignId}/auto-import-links`, { headers: authHeader(), timeout: HOOK_REQUEST_TIMEOUT_MS });
export const postAutoImportLinks = (campaignId, links) =>
  axios.post(`${config.api.base_url}/campaigns/${campaignId}/auto-import-links`, { links }, { headers: authHeader(), timeout: HOOK_REQUEST_TIMEOUT_MS });

// ---------- Thunks ----------

export const getLinksByCampaignApi = (campaignId) => (dispatch) => {
  dispatch({ type: GET_LINKS_BY_CAMPAIGN });
  dedupedGet(`linksByCampaign:${campaignId}`, () =>
    axios.get(`${config.api.base_url}/campaigns/${campaignId}/auto-import-links`, { headers: authHeader() }),
  )
    .then((r) => dispatch({ type: GET_LINKS_BY_CAMPAIGN_SUCCESS, payload: r.data }))
    .catch((e) => dispatch({ type: GET_LINKS_BY_CAMPAIGN_FAIL, payload: e.response?.data || { message: e.message } }));
};
export const getLinksByCampaignReset = () => ({ type: GET_LINKS_BY_CAMPAIGN_RESET });

export const getLinksByProjectApi = (projectId) => (dispatch) => {
  dispatch({ type: GET_LINKS_BY_PROJECT });
  dedupedGet(`linksByProject:${projectId}`, () =>
    axios.get(`${config.api.base_url}/projects/${projectId}/auto-import-links`, { headers: authHeader() }),
  )
    .then((r) => dispatch({ type: GET_LINKS_BY_PROJECT_SUCCESS, payload: r.data }))
    .catch((e) => dispatch({ type: GET_LINKS_BY_PROJECT_FAIL, payload: e.response?.data || { message: e.message } }));
};
export const getLinksByProjectReset = () => ({ type: GET_LINKS_BY_PROJECT_RESET });

export const getLinkableProjectsApi = (campaignId, search = '') => (dispatch) => {
  dispatch({ type: GET_LINKABLE_PROJECTS });
  axios
    .get(`${config.api.base_url}/campaigns/${campaignId}/linkable-projects`, {
      headers: authHeader(),
      params: search ? { search } : {},
    })
    .then((r) => dispatch({ type: GET_LINKABLE_PROJECTS_SUCCESS, payload: r.data }))
    .catch((e) => dispatch({ type: GET_LINKABLE_PROJECTS_FAIL, payload: e.response?.data || { message: e.message } }));
};
export const getLinkableProjectsReset = () => ({ type: GET_LINKABLE_PROJECTS_RESET });

export const saveAutoImportLinksApi = (campaignId, links) => (dispatch) => {
  dispatch({ type: SAVE_AUTO_IMPORT_LINKS });
  axios
    .post(
      `${config.api.base_url}/campaigns/${campaignId}/auto-import-links`,
      { links },
      { headers: authHeader() }
    )
    .then((r) => dispatch({ type: SAVE_AUTO_IMPORT_LINKS_SUCCESS, payload: r.data }))
    .catch((e) =>
      dispatch({ type: SAVE_AUTO_IMPORT_LINKS_FAIL, payload: e.response?.data || { message: e.message } })
    );
};
export const saveAutoImportLinksReset = () => ({ type: SAVE_AUTO_IMPORT_LINKS_RESET });

export const forceSyncFromCampaignApi = (campaignId) => (dispatch) => {
  dispatch({ type: FORCE_SYNC_FROM_CAMPAIGN });
  axios
    .post(`${config.api.base_url}/campaigns/${campaignId}/force-sync`, {}, { headers: authHeader() })
    .then((r) => dispatch({ type: FORCE_SYNC_FROM_CAMPAIGN_SUCCESS, payload: r.data }))
    .catch((e) =>
      dispatch({ type: FORCE_SYNC_FROM_CAMPAIGN_FAIL, payload: e.response?.data || { message: e.message } })
    );
};
export const forceSyncFromCampaignReset = () => ({ type: FORCE_SYNC_FROM_CAMPAIGN_RESET });

export const forceSyncFromProjectApi = (projectId) => (dispatch) => {
  dispatch({ type: FORCE_SYNC_FROM_PROJECT });
  axios
    .post(`${config.api.base_url}/projects/${projectId}/force-sync`, {}, { headers: authHeader() })
    .then((r) => dispatch({ type: FORCE_SYNC_FROM_PROJECT_SUCCESS, payload: r.data }))
    .catch((e) =>
      dispatch({ type: FORCE_SYNC_FROM_PROJECT_FAIL, payload: e.response?.data || { message: e.message } })
    );
};
export const forceSyncFromProjectReset = () => ({ type: FORCE_SYNC_FROM_PROJECT_RESET });

export const getAutoSyncStatusApi = (projectId) => (dispatch) => {
  dispatch({ type: GET_AUTO_SYNC_STATUS });
  axios
    .get(`${config.api.base_url}/projects/${projectId}/auto-sync-status`, { headers: authHeader() })
    .then((r) => dispatch({ type: GET_AUTO_SYNC_STATUS_SUCCESS, payload: r.data }))
    .catch((e) =>
      dispatch({ type: GET_AUTO_SYNC_STATUS_FAIL, payload: e.response?.data || { message: e.message } })
    );
};
export const getAutoSyncStatusReset = () => ({ type: GET_AUTO_SYNC_STATUS_RESET });
